import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Upload, QrCode, Award } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import Switch from 'components/ui/switch';
import { PageHeader } from 'components/ui/page-header';
import * as TestModule from 'modules/test';
import * as AuthModule from 'modules/auth';
import { cn } from 'lib/utils';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';
import { UpgradePrompt } from 'components/entitlements';
import {
  CERT_TEMPLATES,
  MERGE_FIELDS,
  readCertState,
  writeCertState,
  type CertState,
  type CertTemplate,
  type CertTemplateId,
} from './certificate/certificateConfig';
import CertificatePreviewA4 from './certificate/CertificatePreviewA4';

const eq = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

// A tiny certificate mock used as the gallery thumbnail — the template's colors
// on a white card with the same double-border + centered marks as the real one.
const TemplateThumb: React.FC<{ template: CertTemplate }> = ({ template }) => (
  <div
    className="relative flex aspect-[7/5] w-full items-center justify-center rounded-sm bg-white"
    style={{ border: `2px solid ${template.outer}` }}
  >
    <div className="absolute inset-1 rounded-[2px]" style={{ border: `1px solid ${template.inner}` }} aria-hidden />
    <div className="relative flex flex-col items-center gap-1">
      <span className="h-1 w-10 rounded-full" style={{ backgroundColor: template.outer }} />
      <span className="h-1 w-6 rounded-full" style={{ backgroundColor: template.accentText, opacity: 0.5 }} />
    </div>
  </div>
);

export const CertificateTemplatePage: React.FC = () => {
  const { t } = useTranslation();
  const { testId } = useParams<{ testId: string }>();
  const {
    state: { user: profile, organization },
  } = AuthModule.Context.useAuthContext();

  const { item: test, isLoading } = TestModule.Hooks.useSingle({ id: testId || '', enabled: Boolean(testId) });
  const updateConfig = TestModule.Hooks.useUpdateConfig();

  // Certificates are a paid capability (Growth+) — issuing/exporting a
  // certificate is gated on the backend, so gate the template designer too.
  const canCertificates = useFeature(FEATURES.CERTIFICATES);
  const openUpgrade = useOpenUpgradeForFeature();

  const [state, setState] = useState<CertState>(() => readCertState());
  const baseline = useRef<CertState | null>(null);

  useEffect(() => {
    if (!test) return;
    const contentOptions = (test as { config?: { certificate?: { contentOptions?: Record<string, boolean> } } })?.config
      ?.certificate?.contentOptions;
    const next = readCertState(contentOptions);
    setState(next);
    baseline.current = next;
  }, [test]);

  const dirty = baseline.current ? !eq(state, baseline.current) : false;
  const template = CERT_TEMPLATES.find((tp) => tp.id === state.template) ?? CERT_TEMPLATES[0];
  const verifyUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/verify/${testId ?? ''}`;

  const orgName = organization?.name ?? '';
  const testTitle = (test as { title?: string })?.title ?? '';
  const instructorName = profile?.name ?? '';
  const passThreshold =
    (test as { settings?: { passingScore?: number }; passingScore?: number })?.settings?.passingScore ??
    (test as { passingScore?: number })?.passingScore;

  const setTemplate = (id: CertTemplateId) => setState((s) => ({ ...s, template: id }));
  const toggleField = (id: string) => setState((s) => ({ ...s, fields: { ...s.fields, [id]: !s.fields[id] } }));

  const handleSave = async () => {
    if (!testId || !dirty) return;
    try {
      await updateConfig.mutateAsync({
        id: testId,
        step: 'certificate',
        data: { contentOptions: writeCertState(state) },
      });
      baseline.current = state;
    } catch {
      // toast handled by useUpdateConfig
    }
  };

  if (isLoading || !test) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      </div>
    );
  }

  if (!canCertificates) {
    return (
      <div className="flex min-h-[300px] items-center justify-center p-4">
        <UpgradePrompt
          featureKey={FEATURES.CERTIFICATES}
          requiredPlan="GROWTH"
          icon={Award}
          description={t(
            'certificates_premium_desc',
            'Issue branded completion certificates your candidates can download as PDF the moment they pass.',
          )}
          onUpgrade={() => openUpgrade(FEATURES.CERTIFICATES)}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader title={t('certificate')} description={t('cert_template_subtitle')} />

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Template gallery */}
        <div className="w-full shrink-0 lg:w-[180px]">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('cert_template_gallery')}
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-1">
            {CERT_TEMPLATES.map((tp) => {
              const selected = tp.id === state.template;
              return (
                <button
                  key={tp.id}
                  type="button"
                  onClick={() => setTemplate(tp.id)}
                  aria-pressed={selected}
                  className={cn(
                    'group rounded-lg border p-2 text-left transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    selected
                      ? 'border-foreground ring-1 ring-foreground'
                      : 'border-border hover:border-foreground/20',
                  )}
                >
                  <TemplateThumb template={tp} />
                  <div className="mt-2 flex items-center justify-between gap-1 px-0.5">
                    <span className="truncate text-xs font-medium text-foreground">{t(tp.nameKey)}</span>
                    {selected && <Check className="h-3.5 w-3.5 shrink-0 text-foreground" />}
                  </div>
                </button>
              );
            })}
            {/* Custom-template upload isn't wired to a backend yet — shown disabled. */}
            <button
              type="button"
              disabled
              title={t('coming_soon')}
              className="flex aspect-[7/5] w-full cursor-not-allowed flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border text-xs font-medium text-muted-foreground/70 sm:col-span-3 lg:col-span-1 lg:aspect-auto lg:py-3"
            >
              <Upload className="h-4 w-4" />
              {t('cert_upload_template')}
            </button>
          </div>
        </div>

        {/* Live A4 preview */}
        <div className="flex min-w-0 flex-1 items-start justify-center">
          <Card className="w-full bg-muted/30">
            <CardContent className="flex justify-center p-5 sm:p-8">
              <CertificatePreviewA4
                template={template}
                state={state}
                verifyUrl={verifyUrl}
                orgName={orgName}
                testTitle={testTitle}
                instructorName={instructorName}
              />
            </CardContent>
          </Card>
        </div>

        {/* Issue & fields panel */}
        <div className="w-full shrink-0 space-y-5 lg:w-[280px]">
          <p className="text-sm font-semibold text-foreground">{t('cert_issue_and_fields')}</p>

          <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-medium text-foreground">
                {typeof passThreshold === 'number' && passThreshold > 0
                  ? t('cert_issue_on_pass_threshold', { value: passThreshold })
                  : t('cert_issue_on_pass')}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{t('cert_issue_on_pass_desc')}</p>
            </div>
            <Switch checked={state.issueOnPass} onChange={(v) => setState((s) => ({ ...s, issueOnPass: v }))} />
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('cert_merge_fields')}
            </p>
            <p className="mb-2 text-xs text-muted-foreground">{t('cert_insert_field_hint')}</p>
            <div className="flex flex-wrap gap-1.5">
              {MERGE_FIELDS.map((f) => {
                const on = Boolean(state.fields[f.id]);
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => toggleField(f.id)}
                    aria-pressed={on}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-md border px-2 py-1 font-mono text-xs transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                      on
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-muted/50 text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {on && <Check className="h-3 w-3" />}
                    {f.token}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
            <div className="flex items-center gap-2">
              <QrCode className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-foreground">{t('cert_qr_verification')}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{t('cert_qr_verification_desc')}</p>
              </div>
            </div>
            <Switch checked={state.qrEnabled} onChange={(v) => setState((s) => ({ ...s, qrEnabled: v }))} />
          </div>

          <p className="text-xs text-muted-foreground">{t('cert_backend_note')}</p>

          <Button onClick={handleSave} disabled={!dirty || updateConfig.isPending} className="w-full">
            {updateConfig.isPending && (
              <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            )}
            {t('cert_save_template')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CertificateTemplatePage;
